//TODO: this should be in sync with electron version

import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { type ElasticClientConfig, type CustomDimensions, type TelemetryMetadata, type TelemetryUserData } from '@/features/telemetry/types'

export enum LogLevel {
	INFO = 'info',
	WARNING = 'warning',
	ERROR = 'error'
}

export interface ECSLog {
	'@timestamp': string
	message: string
	log: {
		level: string
		logger: string
	}
	ecs: {
		version: string
	}
	service: {
		name: string
		environment: string
	}
	event: {
		type?: string
		dataset: string
	}

	desktop_event?: {
		process: string
		saasPlanId?: string
		saasPlanName?: string
		installationId?: string
		installationPath?: string
		appVersion?: string
		lastAuthenticatedUser?: string
	} & CustomDimensions
	user?: TelemetryUserData

	error?: {
		message: string
		name: string
		cause?: string
		stack?: string
	}
}

export class ElasticClient {
	private url: string
	private apiKey: string
	private metadata: TelemetryMetadata | undefined
	private axiosInstance: AxiosInstance
	private config: ElasticClientConfig

	constructor({ url, apiKey, config }: { url: string; apiKey: string; config: ElasticClientConfig }) {
		this.url = url
		this.apiKey = apiKey
		this.config = config

		this.axiosInstance = axios.create({
			baseURL: this.url
		})
	}

	private formatECSLog(
		level: LogLevel,
		message: string,
		process: 'renderer' | 'main',
		type?: string,
		customDimensions?: CustomDimensions,
		metadata?: TelemetryMetadata,
		error?: Error
	): ECSLog {
		return {
			'@timestamp': new Date().toISOString(),
			message,
			log: {
				level: level,
				logger: this.config.logger
			},
			ecs: {
				version: '8.10.0'
			},
			service: {
				name: this.config.serviceName,
				environment: metadata?.app?.environment ?? 'Unknown'
			},
			event: {
				dataset: this.config.eventDataset,
				type: type
			},
			user: metadata?.user ?? undefined,
			desktop_event: {
				...customDimensions,
				process,
				saasPlanId: metadata?.saasPlan?.id,
				saasPlanName: metadata?.saasPlan?.id,
				appVersion: metadata?.app?.version,
				installationId: metadata?.app?.installationId,
				installationPath: metadata?.app?.installationPath,
				lastAuthenticatedUser: metadata?.app?.lastAuthenticatedUser
			},
			error: error
				? {
						message: error.message,
						name: error.name,
						stack: error.stack
					}
				: undefined
		}
	}

	setMetadata(metadata: TelemetryMetadata) {
		this.metadata = metadata
	}

	async sendLog(level: LogLevel, message: string, process: 'renderer' | 'main', type?: string, customDimensions?: CustomDimensions, error?: Error) {
		const ecsLog = this.formatECSLog(level, message, process, type, customDimensions, this.metadata, error)
		const indexData = { create: { _index: `logs-electron_app-${import.meta.env.VITE_ENVIRONMENT}` } }
		const options: AxiosRequestConfig = {
			url: '/_bulk?pretty',
			method: 'POST',
			headers: {
				Authorization: `ApiKey ${this.apiKey}`,
				'Content-Type': 'application/x-ndjson'
			},
			data: `${JSON.stringify(indexData)}\n${JSON.stringify(ecsLog)}\n`
		}

		try {
			const res = await this.axiosInstance.request(options)

			if (res.data.errors === true) {
				console.error('Error with sending Elastic logs', res.data.items)
			}
		} catch (e) {
			console.error('Error with sending Elastic logs', e)
		}
	}
}
